import React from "react";
import { useImmer } from "use-immer";

import { IgSelect, IgTextField, IgTypography } from "@intelligems/ig-theme";
import { OnboardingInfoFe } from "@intelligems/ig-types";
import { MenuItem, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { getMixpanelStepTitle } from "@src/components/MainApp/Onboarding/utils";
import { useViewPage } from "@src/hooks/mixpanel";

const onboardingRoles = [
  "Ecommerce Founder/Senior Leader",
  "Growth/Marketing Leader",
  "Product Manager",
  "Website/Ecomm Manager",
  "Marketing Manager",
  "Data/Analytics Manager",
  "Developer",
  "Agency User",
  "Other",
];

const agencyOperators: string[] = [
  "I will be, on behalf of the merchant",
  "I will be, this is a portfolio brand",
  "The merchant will be, I am just getting Intelligems set up for them",
];

const teamSizeOptions = {
  "Agency User": [
    "Full Service Agency",
    "CRO Agency",
    "Technical Agency",
    "Paid Media/Marketing Agency",
  ],
  Developer: [
    "Just me or a small team",
    "Full team with dedicated developers/designers",
  ],
  Other: [
    "Just me or a small team (no dedicated technical resources)",
    "Team with some technical resources",
    "Full team with dedicated developers/designers",
  ],
};

const experienceLevels = [
  "No, I'm new to this",
  "Yes, I've tried it a bit",
  "Yes, I've done a lot",
];

interface OnboardingAboutYouProps {
  data: Pick<
    OnboardingInfoFe,
    | "preferredName"
    | "preferredEmail"
    | "companyRole"
    | "otherRole"
    | "teamDescription"
    | "agencyPrimaryOperator"
    | "previousExperience"
  >;
  onChange: (field: keyof OnboardingInfoFe, value: string) => void;
}

export const OnboardingUserInfo = ({
  data,
  onChange,
}: OnboardingAboutYouProps) => {
  const theme = useTheme();
  const [hasModifiedName, setHasModifiedName] = useImmer(false);
  const [hasModifiedEmail, setHasModifiedEmail] = useImmer(false);

  const handleNameChange = (value: string) => {
    setHasModifiedName(true);
    onChange("preferredName", value);
  };

  const handleEmailChange = (value: string) => {
    setHasModifiedEmail(true);
    onChange("preferredEmail", value);
  };

  const handleRoleChange = (value: string) => {
    if (value !== "Other" && data.otherRole) {
      onChange("otherRole", "");
    }
    onChange("companyRole", value);
    onChange("teamDescription", "ig-empty"); // Reset team size when role changes
    if (value !== "Agency User") {
      onChange("agencyPrimaryOperator", "ig-empty"); // Reset agency operator when role is not Agency User
    }
  };

  useViewPage(`Onboarding - ${getMixpanelStepTitle(0)}`);
  return (
    <Stack
      sx={{
        ".MuiBackdrop-root": {
          backgroundColor: "transparent !important",
        },
      }}
      direction={"column"}
      spacing={6}
      width={"100%"}
    >
      <Stack direction={"column"} spacing={2}>
        <IgTypography variant={"h1"} fontWeight={"600"}>
          About you and your team
        </IgTypography>
        <IgTypography color={theme.palette.text.secondary}>
          Help us personalize your Intelligems experience by telling us a bit
          about yourself and your team. Your answers will help us tailor the
          platform to your specific needs and expertise level.
        </IgTypography>
      </Stack>
      <Stack spacing={4}>
        <Stack spacing={2}>
          <IgTextField
            fullWidth={true}
            label="What should we call you?"
            placeholder="Enter your preferred name"
            value={data.preferredName}
            onChange={(e) => handleNameChange(e.target.value)}
            required
          />
          {!hasModifiedName && (
            <IgTypography color={theme.palette.text.accent}>
              We got these details from your Shopify account. Feel free to
              update them if needed.
            </IgTypography>
          )}
        </Stack>
        <Stack spacing={2}>
          <IgTextField
            fullWidth={true}
            label="What is the best email to use to reach you?"
            placeholder="Enter your email address"
            type="email"
            value={data.preferredEmail}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
          />
          {!hasModifiedEmail && (
            <IgTypography color={theme.palette.text.accent}>
              We got these details from your Shopify account. Feel free to
              update them if needed.
            </IgTypography>
          )}
        </Stack>
        <IgSelect
          fullWidth={true}
          placeholder={"Select an option"}
          label="Which of these best describes your role?"
          value={data.companyRole === null ? "ig-empty" : data.companyRole}
          onChange={(e) => handleRoleChange(e.target.value)}
          required
        >
          {onboardingRoles.map((role, index) => (
            <MenuItem key={index} value={role}>
              {role}
            </MenuItem>
          ))}
        </IgSelect>
        {data.companyRole === "Other" && (
          <IgTextField
            fullWidth={true}
            label="Please specify your role"
            placeholder="Enter your role"
            value={data.otherRole || ""}
            onChange={(e) => onChange("otherRole", e.target.value)}
            required
          />
        )}
        <IgSelect
          fullWidth={true}
          label="What best describes your team?"
          placeholder={"Select an option"}
          value={data.teamDescription ?? "ig-empty"}
          onChange={(e) => onChange("teamDescription", e.target.value)}
          required
        >
          {teamSizeOptions[
            data.companyRole === "Agency User" ||
            data.companyRole === "Developer"
              ? data.companyRole
              : "Other"
          ].map((size, index) => (
            <MenuItem key={index} value={size}>
              {size}
            </MenuItem>
          ))}
        </IgSelect>
        {data.companyRole === "Agency User" && (
          <IgSelect
            fullWidth={true}
            placeholder={"Select an option"}
            label="Who will be the primary operator?"
            value={data.agencyPrimaryOperator ?? "ig-empty"}
            onChange={(e) => onChange("agencyPrimaryOperator", e.target.value)}
            required
          >
            {agencyOperators.map((operators, index) => (
              <MenuItem key={index} value={operators}>
                {operators}
              </MenuItem>
            ))}
          </IgSelect>
        )}
        <IgSelect
          fullWidth={true}
          placeholder={"Select an option"}
          label="Do you have previous experience with A/B testing and personalizations?"
          value={data.previousExperience ?? "ig-empty"}
          onChange={(e) => onChange("previousExperience", e.target.value)}
          required
        >
          {experienceLevels.map((level, index) => (
            <MenuItem key={index} value={level}>
              {level}
            </MenuItem>
          ))}
        </IgSelect>
      </Stack>
    </Stack>
  );
};
